import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight, Tag } from 'lucide-react';

export default function Cart() {
  const { cartItems, cartTotal, removeFromCart, updateQuantity, clearCart, fetchCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState(user?.address || '');
  const [placingOrder, setPlacingOrder] = useState(false);

  const SHIPPING = cartTotal > 999 ? 0 : 49;
  const TAX = cartTotal * 0.08;
  const GRAND_TOTAL = cartTotal + SHIPPING + TAX;

  const handlePlaceOrder = async () => {
    if (!address.trim()) {
      toast.error('Please enter a shipping address');
      return;
    }
    setPlacingOrder(true);
    try {
      await API.post('/orders/', { shipping_address: address });
      await fetchCart();
      toast.success('Order placed successfully! 🎉');
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to place order');
    } finally {
      setPlacingOrder(false);
    }
  };

  if (cartItems.length === 0) {
    return (
      <div style={{
        minHeight: 'calc(100vh - 70px)',
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'var(--cream)', gap: '1rem'
      }}>
        <ShoppingBag size={64} style={{ color: 'var(--light-gray)' }} />
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.8rem', color: 'var(--charcoal)' }}>
          Your cart is empty
        </h2>
        <p style={{ color: 'var(--mid-gray)' }}>Add some items to get started</p>
        <Link to="/" style={{
          marginTop: '0.5rem',
          background: 'var(--charcoal)', color: 'var(--white)',
          padding: '0.75rem 2rem', borderRadius: '10px',
          fontWeight: '600', fontSize: '0.95rem',
          display: 'flex', alignItems: 'center', gap: '8px'
        }}>
          <ShoppingBag size={18} /> Continue Shopping
        </Link>
      </div>
    );
  }

  return (
    <div style={{ background: 'var(--cream)', minHeight: 'calc(100vh - 70px)', padding: '2rem' }}>
      <div style={{ maxWidth: '1100px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '2rem', color: 'var(--charcoal)' }}>
            Your Cart
          </h1>
          <button onClick={clearCart} style={{
            background: 'transparent', border: '1px solid var(--mid-gray)',
            color: 'var(--mid-gray)', padding: '0.4rem 1rem',
            borderRadius: '8px', fontSize: '0.85rem',
            display: 'flex', alignItems: 'center', gap: '5px',
            cursor: 'pointer'
          }}>
            <Trash2 size={14} /> Clear All
          </button>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: '2rem', alignItems: 'start' }}>

          {/* Cart Items */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            {cartItems.map(item => (
              <div key={item.id} style={{
                background: 'var(--white)', borderRadius: 'var(--radius)',
                padding: '1.2rem', display: 'flex', gap: '1.2rem',
                alignItems: 'center', boxShadow: 'var(--shadow-sm)'
              }}>
                {/* Image */}
                <img
                  src={item.product.image_url || 'https://via.placeholder.com/100'}
                  alt={item.product.name}
                  style={{
                    width: '90px', height: '90px',
                    objectFit: 'cover', borderRadius: '10px',
                    flexShrink: 0
                  }}
                />

                {/* Details */}
                <div style={{ flex: 1 }}>
                  <h3 style={{
                    fontSize: '1rem', fontWeight: '600',
                    fontFamily: 'var(--font-display)', marginBottom: '0.2rem'
                  }}>{item.product.name}</h3>
                  <div style={{ display: 'flex', gap: '1rem', marginBottom: '0.6rem' }}>
                    {item.size && (
                      <span style={{
                        fontSize: '0.8rem', background: 'var(--light-gray)',
                        padding: '0.15rem 0.6rem', borderRadius: '20px',
                        color: 'var(--charcoal)', fontWeight: '500'
                      }}>Size: {item.size}</span>
                    )}
                    {item.product.category && (
                      <span style={{ fontSize: '0.8rem', color: 'var(--mid-gray)' }}>
                        {item.product.category.name}
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: '1.1rem', fontWeight: '700', color: 'var(--warm-brown)' }}>
                    ₹{parseFloat(item.product.price).toFixed(2)}
                  </span>
                </div>

                {/* Quantity + Remove */}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.8rem' }}>
                  {/* Quantity Controls */}
                  <div style={{
                    display: 'flex', alignItems: 'center', gap: '0.5rem',
                    border: '1.5px solid var(--light-gray)', borderRadius: '8px',
                    padding: '0.2rem 0.4rem'
                  }}>
                    <button onClick={() => updateQuantity(item.id, item.quantity - 1)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--charcoal)', display: 'flex' }}>
                      <Minus size={16} />
                    </button>
                    <span style={{ minWidth: '24px', textAlign: 'center', fontWeight: '600', fontSize: '0.95rem' }}>
                      {item.quantity}
                    </span>
                    <button onClick={() => updateQuantity(item.id, item.quantity + 1)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--charcoal)', display: 'flex' }}>
                      <Plus size={16} />
                    </button>
                  </div>

                  {/* Subtotal */}
                  <span style={{ fontSize: '0.9rem', color: 'var(--mid-gray)', fontWeight: '500' }}>
                    ₹{(parseFloat(item.product.price) * item.quantity).toFixed(2)}
                  </span>

                  {/* Remove */}
                  <button onClick={() => removeFromCart(item.id)}
                    style={{
                      background: 'none', border: 'none',
                      color: 'var(--danger)', cursor: 'pointer', display: 'flex'
                    }}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}

            {/* Continue Shopping */}
            <Link to="/" style={{
              display: 'inline-flex', alignItems: 'center', gap: '6px',
              color: 'var(--warm-brown)', fontWeight: '500', fontSize: '0.9rem',
              marginTop: '0.5rem'
            }}>
              ← Continue Shopping
            </Link>
          </div>

          {/* Order Summary */}
          <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius)',
            padding: '1.8rem', boxShadow: 'var(--shadow-sm)',
            position: 'sticky', top: '90px'
          }}>
            <h2 style={{
              fontFamily: 'var(--font-display)', fontSize: '1.4rem',
              marginBottom: '1.5rem', color: 'var(--charcoal)'
            }}>Order Summary</h2>

            {/* Line Items */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.8rem', marginBottom: '1.2rem' }}>
              {cartItems.map(item => (
                <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                  <span style={{ color: 'var(--mid-gray)' }}>
                    {item.product.name} × {item.quantity}
                    {item.size && <span style={{ color: 'var(--soft-gold)' }}> ({item.size})</span>}
                  </span>
                  <span style={{ fontWeight: '500' }}>
                    ₹{(parseFloat(item.product.price) * item.quantity).toFixed(2)}
                  </span>
                </div>
              ))}
            </div>

            <hr style={{ border: 'none', borderTop: '1px solid var(--light-gray)', margin: '1rem 0' }} />

            {/* Totals */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginBottom: '1.2rem' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--mid-gray)' }}>Subtotal</span>
                <span>₹{cartTotal.toFixed(2)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--mid-gray)' }}>
                  Shipping {SHIPPING === 0 && <span style={{ color: 'var(--success)', fontSize: '0.78rem' }}>FREE</span>}
                </span>
                <span>{SHIPPING === 0 ? 'Free' : `₹₹{SHIPPING.toFixed(2)}`}</span>
              </div>
              {SHIPPING > 0 && (
                <p style={{ fontSize: '0.75rem', color: 'var(--mid-gray)' }}>
                  <Tag size={11} style={{ verticalAlign: 'middle' }} /> Free shipping on orders over ₹999
                </p>
              )}
              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.9rem' }}>
                <span style={{ color: 'var(--mid-gray)' }}>Tax (8%)</span>
                <span>₹{TAX.toFixed(2)}</span>
              </div>
            </div>

            <hr style={{ border: 'none', borderTop: '2px solid var(--charcoal)', margin: '1rem 0' }} />

            {/* Grand Total */}
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
              <span style={{ fontWeight: '700', fontSize: '1.1rem' }}>Total</span>
              <span style={{ fontWeight: '700', fontSize: '1.2rem', color: 'var(--warm-brown)' }}>
                ₹{GRAND_TOTAL.toFixed(2)}
              </span>
            </div>

            {/* Shipping Address */}
            <div style={{ marginBottom: '1.2rem' }}>
              <label style={{ display: 'block', fontWeight: '600', fontSize: '0.88rem', marginBottom: '0.5rem' }}>
                Shipping Address *
              </label>
              <textarea
                value={address}
                onChange={e => setAddress(e.target.value)}
                placeholder="Enter your full delivery address..."
                rows={3}
                style={{
                  width: '100%', padding: '0.7rem',
                  border: '1.5px solid var(--light-gray)',
                  borderRadius: '8px', fontSize: '0.88rem',
                  outline: 'none', resize: 'vertical',
                  fontFamily: 'var(--font-body)',
                  background: 'var(--cream)'
                }}
                onFocus={e => e.target.style.borderColor = 'var(--warm-brown)'}
                onBlur={e => e.target.style.borderColor = 'var(--light-gray)'}
              />
            </div>

            {/* Checkout Button */}
            <button
              onClick={handlePlaceOrder}
              disabled={placingOrder}
              style={{
                width: '100%', padding: '0.9rem',
                background: placingOrder ? 'var(--mid-gray)' : 'var(--charcoal)',
                color: 'var(--white)', border: 'none',
                borderRadius: '10px', fontWeight: '700',
                fontSize: '1rem', cursor: placingOrder ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center',
                justifyContent: 'center', gap: '8px',
                transition: 'var(--transition)'
              }}
              onMouseOver={e => { if (!placingOrder) e.currentTarget.style.background = 'var(--warm-brown)'; }}
              onMouseOut={e => { if (!placingOrder) e.currentTarget.style.background = 'var(--charcoal)'; }}
            >
              {placingOrder ? 'Placing Order...' : <>Place Order <ArrowRight size={18} /></>}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}